import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MessageSquare, Plus, User, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatsListPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchChats = async () => {
      try {
        const response = await axios.get(`${API}/chats`, { withCredentials: true });
        setChats(response.data);
      } catch (error) {
        console.error('Error fetching chats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [isAuthenticated, navigate]);

  const getUnreadCount = (chat) => {
    return user?.role === 'agency' ? chat.unread_agency : chat.unread_user;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chats-list-page">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-bold text-slate-900 tracking-tight">
            My Detective
          </Link>
          <Link 
            to={user?.role === 'agency' ? '/agency/dashboard' : '/dashboard'}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            Dashboard
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-6">Messages</h1>

        {chats.length === 0 ? (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No conversations yet</p>
            {user?.role !== 'agency' && (
              <Button 
                onClick={() => navigate('/')}
                className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
              >
                Find Agencies
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 divide-y divide-slate-200">
            {chats.map((chat) => (
              <div
                key={chat.chat_id}
                onClick={() => navigate(`/chat/${chat.chat_id}`)}
                className="p-4 hover:bg-slate-50 cursor-pointer flex items-center gap-4"
                data-testid={`chat-${chat.chat_id}`}
              >
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {user?.role === 'agency' ? (
                    <User className="w-6 h-6 text-slate-600" />
                  ) : (
                    <Building2 className="w-6 h-6 text-slate-600" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-slate-900 truncate">
                      {user?.role === 'agency' ? chat.user_name : chat.agency_name}
                    </h3>
                    {chat.last_message_at && (
                      <span className="text-xs text-slate-500">
                        {new Date(chat.last_message_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{chat.last_message || 'No messages'}</p>
                </div>
                
                {getUnreadCount(chat) > 0 && (
                  <div className="w-6 h-6 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center">
                    {getUnreadCount(chat)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatsListPage;
